import { OverlayModule } from '@angular/cdk/overlay';
import { Component, computed, inject, signal } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';

import { AuthService, ProfileService } from '@tfs/auth';
import { DividerComponent } from '@tfs/material/divider/divider.component';
import { TfsIconDirective, TfsIconName } from '@tfs/material/tfs-icon';
import { DataSyncNavItemComponent } from '@tfs/shared-feature-data-sync';
import { ToolComparisonService } from '@tfs/shared-feature-tool-comparison';

import { isNonProdEnvironment } from '../../environment.config';

import { ProfilePanelComponent } from './profile-panel/profile-panel.component';

interface NavLink {
  name: string;
  icon: TfsIconName;
  route: string;
  disabled?: boolean;
  tooltip?: string;
}

const INACTIVE_TOOLTIP = 'Subscription inactive. Please renew to access this feature.';
const markDisabled = (item: NavLink): NavLink => ({
  ...item,
  disabled: true,
  tooltip: INACTIVE_TOOLTIP,
});

@Component({
  selector: 'app-sidenav',
  imports: [
    RouterModule,
    OverlayModule,
    MatBadgeModule,
    MatListModule,
    MatTooltipModule,
    TfsIconDirective,
    DividerComponent,
    DataSyncNavItemComponent,
    ProfilePanelComponent,
    MatIconModule,
  ],
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.scss',
  host: {
    '[class.mini]': '!isExpanded()',
  },
})
export class SidenavComponent {
  private readonly authService = inject(AuthService);
  private readonly profileService = inject(ProfileService);
  private readonly toolComparisonService = inject(ToolComparisonService);
  private readonly isDevEnvironment = isNonProdEnvironment();

  readonly isExpanded = signal(true);
  protected readonly isProfilePanelOpen = signal(false);
  protected readonly toolComparisonCount = this.toolComparisonService.itemCount;
  private profileHoverTimer: ReturnType<typeof setTimeout> | null = null;
  private profileCloseTimer: ReturnType<typeof setTimeout> | null = null;

  protected readonly profileName = computed(() => {
    const profile = this.profileService.userProfile();
    return `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim() || 'Profile';
  });

  protected readonly dataSyncTooltip = computed(() =>
    this.isSubscriptionInactive() ? INACTIVE_TOOLTIP : ''
  );

  protected readonly isSubscriptionInactive = this.profileService.isSubscriptionInactive;

  protected readonly navLinks = computed<NavLink[][]>(() => {
    const hasWorkOrders = this.profileService.workOrderManagementEnabled();
    const isInactive = this.isSubscriptionInactive;

    const navGroup1: NavLink[] = [
      { name: 'Items', route: '/library/items/my-inventory', icon: 'grid-four' },
      { name: 'Assemblies', route: '/library/assemblies', icon: 'stack' },
    ];
    if (hasWorkOrders) {
      navGroup1.push({
        name: 'Work orders',
        route: '/library/work-order-management/work-orders',
        icon: 'clipboard-text',
      });
    }

    const navGroup2: NavLink[] = [
      {
        name: 'Tool catalog',
        route: 'library/items/tool-library',
        icon: 'book',
      },
    ];
    if (hasWorkOrders) {
      navGroup2.push({
        name: 'Libraries',
        route: '/library/work-order-management/libraries',
        icon: 'database',
      });
    }
    navGroup2.push({
      name: 'Tooling guides',
      route: '/library/marketplace',
      icon: 'book-open-text',
    });

    const navGroup3: NavLink[] = [
      { name: 'Import logs', route: '/library/tool-import/history', icon: 'tray-arrow-down' },
      { name: 'Tool comparison', route: '/library/comparison', icon: 'chart-bar' },
    ];

    const topNavLinks = [navGroup1, navGroup2, navGroup3];
    return isInactive() ? topNavLinks.map(s => s.map(markDisabled)) : topNavLinks;
  });

  protected readonly bottomNavLinks = computed<NavLink[]>(() => {
    const items: NavLink[] = [];
    if (this.profileService.isAdmin()) {
      items.push({ name: 'Store', route: '/store', icon: 'storefront' });
    }
    if (this.isDevEnvironment && this.profileService.testAccountEndpointsEnabled()) {
      items.push({ name: 'Developer', route: '/dev', icon: 'gear-six' });
      items.push({ name: 'Viewer Tester', route: '/dev/viewer-tester', icon: 'grid-1' });
    }
    return items;
  });

  protected toggleNav(): void {
    this.isExpanded.set(!this.isExpanded());
  }

  protected showProfilePanel(): void {
    this.cancelProfileClose();
    this.profileHoverTimer = setTimeout(() => this.isProfilePanelOpen.set(true), 300);
  }

  protected hideProfilePanel(): void {
    if (this.profileHoverTimer) {
      clearTimeout(this.profileHoverTimer);
      this.profileHoverTimer = null;
    }
    this.scheduleProfileClose();
  }

  protected keepProfilePanel(): void {
    this.cancelProfileClose();
  }

  protected dismissProfilePanel(event: MouseEvent): void {
    const target = event.relatedTarget as HTMLElement | null;
    if (target?.closest('.cdk-overlay-container')) return;
    this.scheduleProfileClose();
  }

  protected closeProfilePanel(): void {
    this.cancelProfileClose();
    this.isProfilePanelOpen.set(false);
  }

  protected onLogout(): void {
    this.authService.logout();
  }

  private scheduleProfileClose(): void {
    this.profileCloseTimer = setTimeout(() => this.isProfilePanelOpen.set(false), 200);
  }

  private cancelProfileClose(): void {
    if (this.profileCloseTimer) {
      clearTimeout(this.profileCloseTimer);
      this.profileCloseTimer = null;
    }
  }
}
